import React, { useState, useEffect } from 'react';
import { X, Check, Activity, Clock, Calendar, Info, HelpCircle, Trash2 } from 'lucide-react';
import { Symptom } from '../lib/types';
import { formatIsoDate } from '../lib/dateUtils';
import { BRISTOL_TYPES } from './BristolStoolPicker';
import { Sheet } from './Sheet';

interface PoopLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (symptom: Omit<Symptom, 'id'>) => void;
  editingSymptom?: Symptom | null;
  onUpdate?: (symptom: Symptom) => void;
  onDelete?: (id: string) => void;
}

export const PoopLogModal: React.FC<PoopLogModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingSymptom,
  onUpdate,
  onDelete,
}) => {
  const [selectedType, setSelectedType] = useState<number>(4); // Default ideal type 4
  const [urgency, setUrgency] = useState<'Normal' | 'Urgent' | 'Straining'>('Normal');
  const [comfortRating, setComfortRating] = useState<number>(5); // 1-5
  const [notes, setNotes] = useState('');
  const [showVisualGuide, setShowVisualGuide] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const now = new Date();
  const [entryDate, setEntryDate] = useState(() => formatIsoDate(now));
  const [entryTime, setEntryTime] = useState(() => {
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  });

  useEffect(() => {
    if (editingSymptom) {
      setSelectedType(editingSymptom.stool_type || 4);
      setComfortRating(editingSymptom.overall_wellbeing || 5);
      setNotes(editingSymptom.notes || '');
      const lowerNotes = (editingSymptom.notes || '').toLowerCase();
      if (lowerNotes.includes('urgent') || lowerNotes.includes('rush')) {
        setUrgency('Urgent');
      } else if (lowerNotes.includes('strain') || lowerNotes.includes('hard')) {
        setUrgency('Straining');
      } else {
        setUrgency('Normal');
      }

      if (editingSymptom.timestamp_iso) {
        const d = new Date(editingSymptom.timestamp_iso);
        if (!isNaN(d.getTime())) {
          setEntryDate(formatIsoDate(d));
          const hh = String(d.getHours()).padStart(2, '0');
          const mm = String(d.getMinutes()).padStart(2, '0');
          setEntryTime(`${hh}:${mm}`);
        }
      }
    } else {
      setSelectedType(4);
      setComfortRating(5);
      setUrgency('Normal');
      setNotes('');
      const n = new Date();
      setEntryDate(formatIsoDate(n));
      const hh = String(n.getHours()).padStart(2, '0');
      const mm = String(n.getMinutes()).padStart(2, '0');
      setEntryTime(`${hh}:${mm}`);
    }
  }, [editingSymptom, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const timestampIso = `${entryDate}T${entryTime}:00`;

    // Map Bristol Type to digestive status
    // Type 1-2 = Constipation, 3-4 = Ideal, 5-7 = Loose / Fast transit
    const bloatEquiv = selectedType >= 5 || selectedType <= 2 ? 2 : 1;
    const soreEquiv = urgency === 'Straining' || urgency === 'Urgent' ? 2 : 1;

    if (editingSymptom && onUpdate) {
      onUpdate({
        ...editingSymptom,
        date: entryDate,
        timestamp_iso: timestampIso,
        bloating_score: bloatEquiv,
        soreness_score: soreEquiv,
        overall_wellbeing: comfortRating,
        stool_type: selectedType,
        symptom_tags: `Bowel Movement, Stool Type ${selectedType}`,
        notes: notes.trim() || undefined,
      });
    } else {
      onSave({
        date: entryDate,
        timestamp_iso: timestampIso,
        checkin_type: 'General Daily',
        associated_meal_id: null,
        bloating_score: bloatEquiv,
        soreness_score: soreEquiv,
        overall_wellbeing: comfortRating,
        stool_type: selectedType,
        symptom_tags: `Bowel Movement, Stool Type ${selectedType}`,
        notes: notes.trim() || undefined,
      });
    }

    onClose();
  };

  const currentBristol = BRISTOL_TYPES.find((b) => b.type === selectedType);

  return (
    <Sheet isOpen={isOpen} onClose={onClose} ariaLabel="Log bowel movement">

        {/* Header */}
        <div className="px-6 py-2.5 flex items-center justify-between border-b border-slate-200/60 dark:border-zinc-800">
          <div className="flex items-center space-x-2">
            <Activity className="w-5 h-5 text-slate-700 dark:text-zinc-300" />
            <h2 className="text-xl font-black uppercase tracking-tight">
              {editingSymptom ? 'Edit Bowel Movement' : 'Log Bowel Movement'}
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            {editingSymptom && onDelete && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="w-8 h-8 rounded-full bg-rose-100 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 flex items-center justify-center hover:bg-rose-200 transition-colors"
                title="Delete Entry"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-slate-200/70 dark:bg-zinc-800 flex items-center justify-center text-slate-700 dark:text-zinc-300 hover:text-rose-500 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {showDeleteConfirm && editingSymptom && onDelete && (
          <div className="mx-6 mt-3 p-3 bg-rose-50 dark:bg-rose-950/60 rounded-2xl border border-rose-200 dark:border-rose-800 space-y-2">
            <p className="text-xs text-rose-700 dark:text-rose-200 font-bold">
              Delete this Bowel Movement entry?
            </p>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => {
                  onDelete(editingSymptom.id);
                  onClose();
                }}
                className="px-3 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-bold active:scale-95 transition-all"
              >
                Confirm Delete
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="px-3 py-1.5 bg-slate-200 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 rounded-lg text-xs font-bold"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-4 space-y-5">
          
          {/* Bristol Scale Selector Header */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400 dark:text-zinc-400">
                Bristol Stool Type (1–7)
              </label>
              <button
                type="button"
                onClick={() => setShowVisualGuide(!showVisualGuide)}
                className="text-xs font-bold text-slate-600 dark:text-zinc-300 hover:underline flex items-center space-x-1"
              >
                <HelpCircle className="w-3.5 h-3.5" />
                <span>{showVisualGuide ? 'Hide Guide' : 'Visual Guide'}</span>
              </button>
            </div>

            {/* 1-7 Capsule Buttons */}
            <div className="grid grid-cols-7 gap-1.5 p-1 bg-white dark:bg-[#1A1D24] rounded-2xl border border-slate-200 dark:border-zinc-800">
              {[1, 2, 3, 4, 5, 6, 7].map((num) => {
                const isSelected = selectedType === num;
                return (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setSelectedType(num)}
                    className={`py-3 rounded-xl flex flex-col items-center justify-center transition-all ${
                      isSelected
                        ? num <= 2
                          ? 'bg-amber-500 text-white font-black shadow-sm scale-[1.05]'
                          : num <= 4
                          ? 'bg-slate-950 dark:bg-white text-white dark:text-black font-black shadow-sm scale-[1.05]'
                          : 'bg-rose-500 text-white font-black shadow-sm scale-[1.05]'
                        : 'text-slate-600 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-zinc-800 font-bold'
                    }`}
                  >
                    <span className="text-sm">T{num}</span>
                  </button>
                );
              })}
            </div>

            {/* Current Type Description Card */}
            {currentBristol && (
              <div className="p-3 bg-white dark:bg-[#1A1D24] rounded-2xl border border-slate-200 dark:border-zinc-800/80 text-xs space-y-0.5">
                <div className="flex items-center justify-between font-black text-slate-900 dark:text-white">
                  <span>Type {currentBristol.type}: {currentBristol.name}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                    currentBristol.category === 'Normal'
                      ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
                      : currentBristol.category === 'Constipation'
                      ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300'
                      : 'bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300'
                  }`}>
                    {currentBristol.category}
                  </span>
                </div>
                <p className="text-slate-500 dark:text-zinc-400">{currentBristol.description}</p>
              </div>
            )}
          </div>

          {/* Stool Chart Visual Guide Accordion */}
          {showVisualGuide && (
            <div className="p-4 bg-white dark:bg-[#1A1D24] rounded-3xl border border-slate-200 dark:border-zinc-800 space-y-2.5 animate-fade-in text-xs">
              <h4 className="font-black uppercase tracking-wider text-slate-900 dark:text-white">
                Bristol Stool Reference Guide
              </h4>
              <div className="space-y-1.5 divide-y divide-slate-100 dark:divide-zinc-800">
                {BRISTOL_TYPES.map((b) => (
                  <div
                    key={b.type}
                    onClick={() => setSelectedType(b.type)}
                    className="pt-1.5 pb-1 flex items-start justify-between cursor-pointer hover:text-emerald-600 transition-colors"
                  >
                    <div>
                      <span className="font-bold">Type {b.type}: </span>
                      <span className="text-slate-500 dark:text-zinc-400">{b.description}</span>
                    </div>
                    <span className="text-[10px] font-bold text-slate-400 ml-2 shrink-0">{b.category}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Urgency & Comfort Selector */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400 dark:text-zinc-400">
                Sensation
              </label>
              <div className="flex bg-white dark:bg-[#1A1D24] p-1 rounded-2xl border border-slate-200 dark:border-zinc-800 text-xs font-bold">
                {(['Normal', 'Urgent', 'Straining'] as const).map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => setUrgency(u)}
                    className={`flex-1 py-2 rounded-xl transition-all ${
                      urgency === u
                        ? 'bg-slate-950 dark:bg-white text-white dark:text-black shadow-xs'
                        : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400 dark:text-zinc-400">
                Comfort (1–5)
              </label>
              <div className="flex bg-white dark:bg-[#1A1D24] p-1 rounded-2xl border border-slate-200 dark:border-zinc-800 text-xs font-bold">
                {[1, 2, 3, 4, 5].map((rate) => (
                  <button
                    key={rate}
                    type="button"
                    onClick={() => setComfortRating(rate)}
                    className={`flex-1 py-2 rounded-xl transition-all ${
                      comfortRating === rate
                        ? 'bg-slate-950 dark:bg-white text-white dark:text-black shadow-xs'
                        : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    {rate}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Date & Time Picker */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400 dark:text-zinc-400 flex items-center space-x-1">
                <Calendar className="w-3 h-3" />
                <span>Date</span>
              </label>
              <input
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                className="w-full px-3 py-2.5 bg-white dark:bg-[#1A1D24] border border-slate-200 dark:border-zinc-800 rounded-2xl text-xs font-bold text-slate-900 dark:text-white focus:outline-none"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400 dark:text-zinc-400 flex items-center space-x-1">
                <Clock className="w-3 h-3" />
                <span>Time</span>
              </label>
              <input
                type="time"
                value={entryTime}
                onChange={(e) => setEntryTime(e.target.value)}
                className="w-full px-3 py-2.5 bg-white dark:bg-[#1A1D24] border border-slate-200 dark:border-zinc-800 rounded-2xl text-xs font-bold text-slate-900 dark:text-white focus:outline-none"
              />
            </div>
          </div>

          {/* Optional Notes */}
          <div className="space-y-1.5">
            <label className="text-xs font-black uppercase tracking-widest text-slate-400 dark:text-zinc-400">
              Personal Notes (Optional)
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Floating, undigested bits, quick relief..."
              className="w-full px-3.5 py-2.5 bg-white dark:bg-[#1A1D24] border border-slate-200 dark:border-zinc-800 rounded-2xl text-xs text-slate-900 dark:text-white focus:outline-none"
            />
          </div>

          {/* Submit Action */}
          <button
            type="submit"
            className="w-full py-4 rounded-2xl bg-slate-950 dark:bg-white text-white dark:text-black font-black text-sm uppercase tracking-wider shadow-lg active:scale-95 transition-all flex items-center justify-center space-x-2"
          >
            <Check className="w-4 h-4 stroke-[3]" />
            <span>{editingSymptom ? 'Update Bowel Movement' : 'Save Bowel Movement'}</span>
          </button>
        </form>
    </Sheet>
  );
};
